import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
import * as XLSX from 'xlsx';
//@ts-ignore
import Modal from 'react-awesome-modal';
import { SvgElement, icontypesEnum } from '../assets/svgElement';

function ManageAudit() {
  const [audits, setAudits] = useState<any>([]);
  const [loader, setLoader] = useState(false);
  const [summary, setSummary] = useState<any>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [role, setRole] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Detail modal
  const [detailModal, setDetailModal] = useState(false);
  const [detailItem, setDetailItem] = useState<any>(null);

  // ── Categorize audit actions ───────────────────────────
  const getCategory = (action: string) => {
    if (!action) return { label: 'System', color: 'bg-gray-100 text-gray-700', icon: '⚙️' };
    const a = action.toLowerCase();

    if (a.includes('confirm') && (a.includes('payment') || a.includes('delivery')))
      return { label: 'Payment Confirmation', color: 'bg-blue-100 text-blue-700', icon: '✓' };
    if (a.includes('approv') && (a.includes('payment') || a.includes('delivery') || a.includes('wallet')))
      return { label: 'Payment Approval', color: 'bg-green-100 text-green-700', icon: '✓✓' };
    if (a.includes('reject') && (a.includes('payment') || a.includes('delivery')))
      return { label: 'Payment Rejection', color: 'bg-red-100 text-red-700', icon: '✗' };
    if (a.includes('fine') || a.includes('penalty'))
      return { label: 'Fine', color: 'bg-orange-100 text-orange-700', icon: '⚠' };
    if (a.includes('reimbursement') || a.includes('reimburse'))
      return { label: 'Reimbursement', color: 'bg-purple-100 text-purple-700', icon: '₦' };
    if (a.includes('withdraw'))
      return { label: 'Withdrawal', color: 'bg-yellow-100 text-yellow-700', icon: '↑' };
    if (a.includes('manifest') || a.includes('runway'))
      return { label: 'Manifest', color: 'bg-cyan-100 text-cyan-700', icon: '📋' };
    if (a.includes('rider') || a.includes('fleet') || a.includes('approv') || a.includes('application'))
      return { label: 'Rider/Fleet', color: 'bg-indigo-100 text-indigo-700', icon: '🏍' };
    if (a.includes('status') || a.includes('shipment') || a.includes('delivery') || a.includes('ofd') || a.includes('pod'))
      return { label: 'Shipment', color: 'bg-teal-100 text-teal-700', icon: '📦' };
    if (a.includes('metro') || a.includes('rate') || a.includes('setting'))
      return { label: 'Settings', color: 'bg-pink-100 text-pink-700', icon: '⚙' };
    if (a.includes('wallet') || a.includes('credit') || a.includes('suspend'))
      return { label: 'Wallet', color: 'bg-emerald-100 text-emerald-700', icon: '💰' };
    if (a.includes('login') || a.includes('logout') || a.includes('password'))
      return { label: 'Auth', color: 'bg-slate-100 text-slate-700', icon: '🔒' };

    return { label: 'System', color: 'bg-gray-100 text-gray-700', icon: '⚙️' };
  };

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'payment', label: 'Payment (Confirm/Approve/Reject)' },
    { value: 'fine', label: 'Fines' },
    { value: 'reimbursement', label: 'Reimbursement' },
    { value: 'manifest', label: 'Manifest' },
    { value: 'shipment', label: 'Shipment Status' },
    { value: 'rider', label: 'Rider/Fleet' },
    { value: 'wallet', label: 'Wallet' },
    { value: 'settings', label: 'Settings' },
  ];

  // ── Load audits ────────────────────────────────────────
  const loadAudits = useCallback((page?: string) => {
    setLoader(true);
    const query: any = {
      search,
      category: category !== 'all' ? category : '',
      role: role !== 'all' ? role : '',
      date_from: dateFrom,
      date_to: dateTo,
      page: page || '1',
      per_page: '25',
    };

    SuperAdminApis.getAudits(page || '', query).then(
      (response: AxiosResponse<any>) => {
        setLoader(false);
        if (response?.data) {
          setAudits(response?.data?.data);
          if (response?.data?.summary) setSummary(response.data.summary);
        }
      }
    ).catch(() => { setLoader(false); toast.error('Failed to load audits.'); });
  }, [search, category, role, dateFrom, dateTo]);

  useEffect(() => { loadAudits(); }, []);

  // ── Compute local summary from current page data ──────
  const localSummary = useMemo(() => {
    if (!audits?.data) return null;
    const cats: any = {};
    audits.data.forEach((a: any) => {
      const cat = getCategory(a.shipment_action);
      cats[cat.label] = (cats[cat.label] || 0) + 1;
    });
    return cats;
  }, [audits?.data]);

  // ── Export ──────────────────────────────────────────────
  const handleExport = () => {
    if (!audits?.data?.length) { toast.error('No data to export.'); return; }
    const exportData = audits.data.map((a: any, i: number) => ({
      'S/N': i + 1,
      'Date': a.created_at ? new Date(a.created_at).toLocaleString() : '—',
      'Auditor': a.auditor_name || '—',
      'Email': a.auditor_email || '—',
      'Role': a.role || '—',
      'Branch': a.branch || '—',
      'Category': getCategory(a.shipment_action).label,
      'Reference': a.shipment_id || '—',
      'Action': a.shipment_action || '—',
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws['!cols'] = [{ wch: 5 }, { wch: 20 }, { wch: 18 }, { wch: 25 }, { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 18 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Audit Log');
    XLSX.writeFile(wb, `audit_log_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`Exported ${exportData.length} records.`);
  };

  // ── Paginate ───────────────────────────────────────────
  const paginate = (label: string) => {
    const page = label === 'Next &raquo;' || label === '&laquo; Previous'
      ? audits?.links?.find((l: any) => l.label === label)?.url?.split('page=')[1]
      : label;
    if (page) loadAudits(page);
  };

  const formatDate = (d: string) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + date.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <AdminSidebar title="Audit Log" />
      <div className="relative md:ml-64 bg-[#FAFAFA]">
        <AdminNavbar title="Audit Log" />

        <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">
          <div className="flex flex-wrap justify-between items-center mt-5 mb-2 gap-2">
            <div>
              <h3 className="text-[15px] text-[#1C1C1C] font-semibold">Audit Log</h3>
              <p className="text-xs text-gray-500">Track all system actions — payments, approvals, shipment changes, and more.</p>
            </div>
            <button onClick={handleExport} className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-1">
              Export Current Page
            </button>
          </div>

          {/* ── Category Summary ──────────────────────────── */}
          {localSummary && Object.keys(localSummary).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(localSummary).sort((a: any, b: any) => b[1] - a[1]).map(([cat, count]: any) => {
                const info = getCategory(cat === 'Payment Confirmation' ? 'confirm payment' : cat === 'Fine' ? 'fine' : cat === 'Manifest' ? 'manifest' : cat);
                return (
                  <div key={cat} className={`${info.color} rounded-lg px-3 py-2 text-xs font-semibold`}>
                    <span className="mr-1">{info.icon}</span> {cat}: {count}
                  </div>
                );
              })}
              <div className="bg-gray-800 text-white rounded-lg px-3 py-2 text-xs font-semibold">
                Total: {audits?.total || audits?.data?.length || 0}
              </div>
            </div>
          )}

          {/* ── Filters ───────────────────────────────────── */}
          <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs text-gray-500">Search</label>
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name, email, shipment, action..."
                  className="w-full border border-gray-300 rounded-md p-2 text-sm" />
              </div>
              <div className="min-w-[180px]">
                <label className="text-xs text-gray-500">Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm">
                  {categoryOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-[120px]">
                <label className="text-xs text-gray-500">Role</label>
                <select value={role} onChange={(e) => setRole(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm">
                  <option value="all">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="fleet">Fleet</option>
                  <option value="rider">Rider</option>
                  <option value="driver">Driver</option>
                  <option value="operations">Operations</option>
                  <option value="customer service">Customer Service</option>
                </select>
              </div>
              <div className="min-w-[130px]">
                <label className="text-xs text-gray-500">From</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm" />
              </div>
              <div className="min-w-[130px]">
                <label className="text-xs text-gray-500">To</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm" />
              </div>
              <button onClick={() => loadAudits()} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">Filter</button>
              {(search || category !== 'all' || role !== 'all' || dateFrom || dateTo) && (
                <button onClick={() => { setSearch(''); setCategory('all'); setRole('all'); setDateFrom(''); setDateTo(''); setTimeout(() => loadAudits(), 50); }}
                  className="text-[#E04343] text-xs font-semibold hover:underline">Clear</button>
              )}
            </div>
          </div>

          {/* ── Table ─────────────────────────────────────── */}
          {loader ? (
            <div className="flex justify-center py-16">
              <Oval visible height="40" width="40" color="#E04343" secondaryColor="#FCBF94" ariaLabel="loading" />
            </div>
          ) : (
            <div className="relative overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-2">
                <thead className="text-[11px] text-[#030229] bg-[#FAFAFA] uppercase">
                  <tr>
                    <th className="px-2 py-2">#</th>
                    <th className="px-2 py-2">Date & Time</th>
                    <th className="px-2 py-2">Category</th>
                    <th className="px-2 py-2">Auditor</th>
                    <th className="px-2 py-2">Role</th>
                    <th className="px-2 py-2">Branch</th>
                    <th className="px-2 py-2">Reference</th>
                    <th className="px-2 py-2">Action</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {audits?.data?.length > 0 ? audits.data.map((a: any, i: number) => {
                    const cat = getCategory(a.shipment_action);
                    return (
                      <tr key={a.id || i} className="bg-white hover:bg-gray-50">
                        <td className="px-2 py-3 text-xs text-gray-400">{i + 1}</td>
                        <td className="px-2 py-3">
                          <div className="text-xs font-semibold text-gray-800">
                            {a.created_at ? new Date(a.created_at).toLocaleDateString('en-NG', { month: 'short', day: 'numeric' }) : '—'}
                          </div>
                          <div className="text-[10px] text-gray-400">
                            {a.created_at ? new Date(a.created_at).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${cat.color}`}>
                            {cat.icon} {cat.label}
                          </span>
                        </td>
                        <td className="px-2 py-3">
                          <div className="text-xs font-semibold">{a.auditor_name || '—'}</div>
                          <div className="text-[10px] text-gray-400 truncate max-w-[140px]">{a.auditor_email || ''}</div>
                        </td>
                        <td className="px-2 py-3">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-semibold capitalize">
                            {a.role || '—'}
                          </span>
                        </td>
                        <td className="px-2 py-3 text-xs">{a.branch || '—'}</td>
                        <td className="px-2 py-3">
                          {a.shipment_id ? (
                            <span className="text-xs font-mono bg-gray-50 px-1.5 py-0.5 rounded text-gray-700">{a.shipment_id}</span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-2 py-3">
                          <p className="text-xs text-gray-700 max-w-[300px] truncate" title={a.shipment_action}>
                            {a.shipment_action || '—'}
                          </p>
                        </td>
                        <td className="px-2 py-3">
                          <button onClick={() => { setDetailItem(a); setDetailModal(true); }}
                            className="text-blue-500 hover:underline text-[10px]">View</button>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={9} className="text-center py-8 text-gray-400">No audit records found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ────────────────────────────────── */}
          <div className="flex justify-end mt-4">
            {audits?.links?.map((link: any, idx: number) => (
              <button key={idx} onClick={() => paginate(link.label)} disabled={link.active || !link.url}
                className={`mx-0.5 py-1 px-2 text-xs ${link.active ? 'bg-[#E04343] text-white' : link.url ? 'bg-gray-200 text-black' : 'bg-gray-100 text-gray-400'}`}>
                {link.label.replace('&laquo; Previous', '< Prev').replace('Next &raquo;', 'Next >')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Detail Modal ──────────────────────────────────── */}
      <Modal visible={detailModal} width="550" height="400" effect="fadeInUp" onClickAway={() => setDetailModal(false)}>
        <div className="p-5 h-full overflow-auto">
          <div className="flex justify-between mb-4">
            <p className="font-bold text-[15px]">Audit Detail</p>
            <p className="cursor-pointer" onClick={() => setDetailModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
          </div>

          {detailItem && (
            <div className="space-y-4">
              {/* Category banner */}
              {(() => {
                const cat = getCategory(detailItem.shipment_action);
                return (
                  <div className={`${cat.color} rounded-lg p-3 text-center`}>
                    <p className="text-sm font-bold">{cat.icon} {cat.label}</p>
                  </div>
                );
              })()}

              {/* Details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Date & Time</span>
                  <span className="font-semibold">{formatDate(detailItem.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Auditor</span>
                  <span className="font-semibold">{detailItem.auditor_name || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="text-blue-600">{detailItem.auditor_email || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Role</span>
                  <span className="capitalize font-semibold">{detailItem.role || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Branch</span>
                  <span>{detailItem.branch || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Reference</span>
                  <span className="font-mono">{detailItem.shipment_id || '—'}</span>
                </div>
              </div>

              {/* Full action text */}
              <div className="bg-white border rounded-lg p-4">
                <p className="text-xs font-bold text-gray-700 uppercase mb-2">Action Performed</p>
                <p className="text-sm text-gray-800 leading-relaxed">{detailItem.shipment_action || '—'}</p>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <ToastContainer position="top-right" autoClose={2000} hideProgressBar />
    </>
  );
}

export default ManageAudit;